import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Phone, ArrowRight, CheckCircle2 } from "lucide-react";

export default function LoginPage() {
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [debugOtp, setDebugOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, API } = useAuth();
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    if (!mobile || mobile.length < 10) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: mobile.trim() })
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Invalid server response");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to send OTP");
      }

      toast.success("OTP sent successfully!");
      if (data.debug_otp) {
        setDebugOtp(data.debug_otp);
      }
      setStep(2);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error("Please enter the complete OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: mobile.trim(), code: otp })
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Invalid server response");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Invalid OTP");
      }

      login(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}!`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {/* Content */}
      <div className="min-h-screen flex">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-between px-16 py-16 bg-[#002e46]">
          <div className="bg-white rounded-lg p-3 inline-block w-fit">
            <img 
              src="/isthara-logo.svg" 
              alt="Isthara" 
              className="h-10 w-auto"
            />
          </div>
          
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
                <div className="w-2 h-2 bg-[#c4a35a] rounded-full" />
                <span className="text-[#c4a35a] text-sm font-medium tracking-wide uppercase">Preopening Management</span>
              </div>
            </div>
            <p className="text-xl text-white/70 max-w-md leading-relaxed">
              Streamline your venue preopening process with elegance and precision. Track projects, manage teams, deliver excellence.
            </p>
            <div className="flex flex-wrap gap-4 text-white/60">
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-[#c4a35a]" />
                <span>Project Tracking</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-[#c4a35a]" />
                <span>SMS Reminders</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-[#c4a35a]" />
                <span>Team Management</span>
              </div>
            </div>
          </div>
          
          <div></div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <Card className="w-full max-w-md bg-white border border-slate-200 shadow-lg" data-testid="login-card">
            <CardHeader className="space-y-3 pb-6">
              <div className="lg:hidden mb-4">
                <img 
                  src="/isthara-logo.svg" 
                  alt="Isthara" 
                  className="h-10 w-auto"
                />
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
                {step === 1 ? "Welcome back" : "Verify OTP"}
              </CardTitle>
              <CardDescription className="text-slate-500">
                {step === 1 
                  ? "Enter your mobile number to continue" 
                  : `We've sent a verification code to ${mobile}`
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === 1 ? (
                <form onSubmit={handleSendOtp} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="mobile" className="text-sm font-medium text-slate-700">
                      Mobile Number
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="mobile"
                        type="tel"
                        placeholder="Enter your mobile number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        className="pl-11 h-12 text-base rounded-lg border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                        data-testid="mobile-input"
                        maxLength={15}
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold rounded-lg gap-2 bg-[#1e3a5f] hover:bg-[#152a47] text-white active:scale-[0.98] transition-all"
                    disabled={loading}
                    data-testid="send-otp-btn"
                  >
                    {loading ? "Sending..." : "Continue"}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-6">
                  <div className="space-y-4">
                    <Label className="text-sm font-medium text-slate-700">
                      Verification Code
                    </Label>
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={otp}
                        onChange={(value) => setOtp(value)}
                        data-testid="otp-input"
                      >
                        <InputOTPGroup className="gap-2">
                          {[0, 1, 2, 3, 4, 5].map((index) => (
                            <InputOTPSlot 
                              key={index} 
                              index={index}
                              className="w-12 h-14 text-xl font-semibold rounded-lg border-2 border-slate-200"
                            />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    {/* Fallback text input */}
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Or enter 6-digit code here"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="text-center text-lg tracking-widest rounded-lg h-12 border-slate-200"
                        maxLength={6}
                        data-testid="otp-text-input"
                      />
                    </div>
                    {debugOtp && (
                      <p className="text-center text-sm text-slate-500">
                        OTP: <span className="font-mono font-bold text-[#1e3a5f]">{debugOtp}</span>
                      </p>
                    )}
                  </div>
                  <div className="space-y-3">
                    <Button 
                      type="submit" 
                      className="w-full h-12 text-base font-semibold rounded-lg gap-2 bg-[#1e3a5f] hover:bg-[#152a47] text-white active:scale-[0.98] transition-all"
                      disabled={loading || otp.length !== 6}
                      data-testid="verify-otp-btn"
                    >
                      {loading ? "Verifying..." : "Verify & Login"}
                      <CheckCircle2 className="w-5 h-5" />
                    </Button>
                    <Button 
                      type="button"
                      variant="ghost"
                      className="w-full h-10 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                      onClick={() => { setStep(1); setOtp(""); setDebugOtp(""); }}
                      data-testid="back-btn"
                    >
                      Change mobile number
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
